import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SearchFilters, Property, SearchResponse } from '../types/property'
import Header from '../components/Header'
import FilterBar from '../components/FilterBar'
import ListingsPanel from '../components/ListingsPanel'
import MapPanel from '../components/MapPanel'
import { debounce } from '../utils/debounce'

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [properties, setProperties] = useState<Property[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null)
  const [showMap, setShowMap] = useState(true) // Mobile toggle
  const [filters, setFilters] = useState<SearchFilters>({})

  // Load filters from URL on mount
  useEffect(() => {
    const filtersFromURL: SearchFilters = {}

    if (searchParams.get('q')) filtersFromURL.q = searchParams.get('q')!
    if (searchParams.get('status')) filtersFromURL.status = searchParams.get('status')!.split(',')
    if (searchParams.get('minPrice')) filtersFromURL.minPrice = Number(searchParams.get('minPrice'))
    if (searchParams.get('maxPrice')) filtersFromURL.maxPrice = Number(searchParams.get('maxPrice'))
    if (searchParams.get('beds')) filtersFromURL.beds = Number(searchParams.get('beds'))
    if (searchParams.get('baths')) filtersFromURL.baths = Number(searchParams.get('baths'))
    if (searchParams.get('propertyType')) filtersFromURL.propertyType = searchParams.get('propertyType')!.split(',')
    if (searchParams.get('minSqft')) filtersFromURL.minSqft = Number(searchParams.get('minSqft'))
    if (searchParams.get('maxSqft')) filtersFromURL.maxSqft = Number(searchParams.get('maxSqft'))
    if (searchParams.get('page')) setPage(Number(searchParams.get('page')))

    setFilters(filtersFromURL)
  }, [])

  // Fetch properties
  const fetchProperties = useCallback(async (currentFilters: SearchFilters, currentPage: number) => {
    setLoading(true)
    setError(null)

    try {
      const queryParams = new URLSearchParams()

      if (currentFilters.q) queryParams.set('q', currentFilters.q)
      if (currentFilters.status) queryParams.set('status', currentFilters.status.join(','))
      if (currentFilters.minPrice) queryParams.set('minPrice', currentFilters.minPrice.toString())
      if (currentFilters.maxPrice) queryParams.set('maxPrice', currentFilters.maxPrice.toString())
      if (currentFilters.beds) queryParams.set('beds', currentFilters.beds.toString())
      if (currentFilters.baths) queryParams.set('baths', currentFilters.baths.toString())
      if (currentFilters.propertyType) queryParams.set('propertyType', currentFilters.propertyType.join(','))
      if (currentFilters.minSqft) queryParams.set('minSqft', currentFilters.minSqft.toString())
      if (currentFilters.maxSqft) queryParams.set('maxSqft', currentFilters.maxSqft.toString())
      if (currentFilters.minYear) queryParams.set('minYear', currentFilters.minYear.toString())
      if (currentFilters.maxYear) queryParams.set('maxYear', currentFilters.maxYear.toString())
      if (currentFilters.bounds) {
        queryParams.set('swLat', currentFilters.bounds.sw.lat.toString())
        queryParams.set('swLng', currentFilters.bounds.sw.lng.toString())
        queryParams.set('neLat', currentFilters.bounds.ne.lat.toString())
        queryParams.set('neLng', currentFilters.bounds.ne.lng.toString())
      }
      queryParams.set('page', currentPage.toString())
      queryParams.set('pageSize', '25')

      const response = await fetch(`/.netlify/functions/search?${queryParams.toString()}`)

      if (!response.ok) {
        throw new Error('Failed to fetch properties')
      }

      const data: SearchResponse = await response.json()

      if (currentPage === 1) {
        setProperties(data.items)
      } else {
        setProperties(prev => [...prev, ...data.items])
      }
      setTotal(data.total)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams()

    if (filters.q) params.set('q', filters.q)
    if (filters.status) params.set('status', filters.status.join(','))
    if (filters.minPrice) params.set('minPrice', filters.minPrice.toString())
    if (filters.maxPrice) params.set('maxPrice', filters.maxPrice.toString())
    if (filters.beds) params.set('beds', filters.beds.toString())
    if (filters.baths) params.set('baths', filters.baths.toString())
    if (filters.propertyType) params.set('propertyType', filters.propertyType.join(','))
    if (filters.minSqft) params.set('minSqft', filters.minSqft.toString())
    if (filters.maxSqft) params.set('maxSqft', filters.maxSqft.toString())
    if (page > 1) params.set('page', page.toString())

    setSearchParams(params, { replace: true })
    fetchProperties(filters, page)
  }, [filters, page])

  const handleFilterChange = (newFilters: SearchFilters) => {
    setFilters(newFilters)
    setPage(1)
    setProperties([])
  }

  const handleMapBoundsChange = useCallback(
    debounce((bounds: { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } }) => {
      setFilters(prev => ({ ...prev, bounds }))
      setPage(1)
    }, 1000),
    []
  )

  const handleLoadMore = () => {
    setPage(prev => prev + 1)
  }

  const handlePropertySelect = (property: Property) => {
    setSelectedProperty(property)
  }

  const handleSaveSearch = () => {
    const url = window.location.href
    navigator.clipboard.writeText(url)
    alert('Search URL copied to clipboard!')
  }

  return (
    <div className="flex flex-col h-screen pt-[72px] md:pt-[88px]">
      {/* Header */}
      <Header />

      {/* Filter Bar */}
      <FilterBar
        filters={filters}
        onChange={handleFilterChange}
        onSaveSearch={handleSaveSearch}
        resultsCount={total}
      />

      {/* Mobile View Toggle */}
      <div className="flex lg:hidden border-b border-gray-200 bg-white">
        <button
          onClick={() => setShowMap(false)}
          className={`flex-1 py-3 text-sm font-medium ${
            !showMap ? 'text-black border-b-2 border-black' : 'text-gray-500'
          }`}
        >
          List
        </button>
        <button
          onClick={() => setShowMap(true)}
          className={`flex-1 py-3 text-sm font-medium ${
            showMap ? 'text-black border-b-2 border-black' : 'text-gray-500'
          }`}
        >
          Map
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Listings Panel */}
        <div className={`${showMap ? 'hidden lg:block' : 'block'} w-full lg:w-2/5 overflow-auto`}>
          <ListingsPanel
            properties={properties}
            loading={loading}
            error={error}
            onLoadMore={handleLoadMore}
            hasMore={properties.length < total}
            onPropertySelect={handlePropertySelect}
            selectedProperty={selectedProperty}
          />
        </div>

        {/* Map Panel */}
        <div className={`${!showMap ? 'hidden lg:block' : 'block'} w-full lg:w-3/5 lg:border-l border-gray-200`}>
          <MapPanel
            properties={properties}
            selectedProperty={selectedProperty}
            onPropertySelect={handlePropertySelect}
            onBoundsChange={handleMapBoundsChange}
          />
        </div>
      </div>
    </div>
  )
}
